// Audit log drawer for an attendance row.
// Spec: docs/admin-attendance-create-edit-history.md §5
// BE returns flat rows sorted DESC; FE groups by `batchId` to show 1 op = N field changes.

import { useEffect, useMemo, useState } from 'react';
import { Drawer, Empty, Pagination, Spin, Tag, Typography, notification } from 'antd';
import dayjs from 'dayjs';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import {
  Attendance,
  AttendanceHistoryAction,
  AttendanceHistoryActor,
  AttendanceHistoryResponse,
  AttendanceHistoryRow,
  CreateSnapshot,
} from 'types/attendance';
import { colors } from 'theme/tokens';
import { mapAttendanceError } from '../helpers/attendance-error';
import { attendancePersonName } from '../helpers/attendance-person';
import AttendancePhotos from './attendance-photos';

const LIMIT = 20;

interface Props {
  open: boolean;
  attendanceId: number | null;
  /** Dòng đang xem (lấy từ bảng) — để hiện tóm tắt + ảnh chấm công ở đầu ngăn kéo. */
  attendance?: Attendance | null;
  onClose: () => void;
}

interface BatchEntry {
  batchId: string;
  action: AttendanceHistoryAction;
  createdAt: string;
  reason: string;
  actor: AttendanceHistoryActor;
  rows: AttendanceHistoryRow[];
}

const FIELD_LABELS: Record<string, string> = {
  checkinTime: 'Giờ checkin',
  checkoutTime: 'Giờ checkout',
  status: 'Trạng thái',
  workingMinutes: 'Số phút làm việc',
  isApproved: 'Đã duyệt',
};

function actorName(a?: AttendanceHistoryActor): string {
  if (!a) return 'Không rõ';
  const full = [a.firstName, a.lastName].filter(Boolean).join(' ').trim();
  return full || a.nickName || a.email || `User #${a.id}`;
}

function formatValue(field: string, raw: string | null): string {
  if (raw == null) return '—';
  if (field === 'checkinTime' || field === 'checkoutTime') {
    return dayjs(raw).format('YYYY-MM-DD HH:mm:ss');
  }
  if (field === 'workingMinutes') return `${raw} phút`;
  if (field === 'isApproved') return raw === 'true' ? 'Có' : 'Không';
  return raw;
}

function groupByBatch(rows: AttendanceHistoryRow[]): BatchEntry[] {
  const map = new Map<string, BatchEntry>();
  // Preserve BE insertion order (DESC).
  for (const r of rows) {
    const existing = map.get(r.batchId);
    if (existing) {
      existing.rows.push(r);
    } else {
      map.set(r.batchId, {
        batchId: r.batchId,
        action: r.action,
        createdAt: r.createdAt,
        reason: r.reason,
        actor: r.actor,
        rows: [r],
      });
    }
  }
  return Array.from(map.values());
}

const AttendanceHistoryDrawer = ({ open, attendanceId, attendance, onClose }: Props) => {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<AttendanceHistoryRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Reset page when drawer opens with a new id.
  useEffect(() => {
    if (open) setPage(1);
  }, [open, attendanceId]);

  useEffect(() => {
    if (!open || !attendanceId) return;
    let cancelled = false;
    const fetchHistory = async () => {
      setLoading(true);
      try {
        const url = `${API.attendanceAdmin}/${attendanceId}/history`;
        const res: any = await AxiosUtils.get(url, { page, limit: LIMIT });
        const body: AttendanceHistoryResponse = res?.data;
        if (cancelled) return;
        setData(body?.data || []);
        setTotal(body?.metadata?.total || 0);
      } catch (err) {
        if (!cancelled) notification.error({ message: mapAttendanceError(err) });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchHistory();
    return () => {
      cancelled = true;
    };
  }, [open, attendanceId, page]);

  const batches = useMemo(() => groupByBatch(data), [data]);

  return (
    <Drawer title="Lịch sử thao tác" open={open} onClose={onClose} width={560} destroyOnClose>
      {attendance && attendance.id === attendanceId && <RecordSummary attendance={attendance} />}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : batches.length === 0 ? (
        <Empty description="Chưa có lịch sử" />
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {batches.map((b) => (
              <BatchCard key={b.batchId} batch={b} />
            ))}
          </div>
          {total > LIMIT && (
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
              <Pagination
                current={page}
                pageSize={LIMIT}
                total={total}
                showSizeChanger={false}
                onChange={setPage}
              />
            </div>
          )}
        </>
      )}
    </Drawer>
  );
};

const clockText = (value?: string | null) => (value ? dayjs(value).format('HH:mm') : '—');

// Tóm tắt bản ghi đang xem: ai, ngày/ca, giờ vào–ra và ảnh khuôn mặt để đối chiếu.
const RecordSummary = ({ attendance }: { attendance: Attendance }) => {
  const name = attendancePersonName(attendance.user);
  const username = attendance.user?.username;
  const shift = attendance.shift;
  const shiftText = shift
    ? `${shift.name}${
        shift.startTime && shift.endTime && !shift.name?.includes(shift.startTime)
          ? ` (${shift.startTime}–${shift.endTime})`
          : ''
      }`
    : '';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        border: `1px solid ${colors.line}`,
        borderRadius: 14,
        padding: '12px 16px',
        marginBottom: 16,
        background: colors.surface,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <Typography.Text strong style={{ display: 'block' }}>
          {name || `Bản ghi #${attendance.id}`}
          {username && name !== username && (
            <Typography.Text type="secondary" style={{ fontWeight: 400, marginLeft: 6 }}>
              {username}
            </Typography.Text>
          )}
        </Typography.Text>
        <Typography.Text type="secondary" style={{ display: 'block', fontSize: 13 }}>
          {dayjs(attendance.date).format('YYYY-MM-DD')}
          {shiftText && ` · ${shiftText}`}
        </Typography.Text>
        <Typography.Text type="secondary" style={{ display: 'block', fontSize: 13 }}>
          Vào {clockText(attendance.checkinTime)} · Ra {clockText(attendance.checkoutTime)}
        </Typography.Text>
      </div>
      <AttendancePhotos record={attendance} personName={name} size={56} />
    </div>
  );
};

const BatchCard = ({ batch }: { batch: BatchEntry }) => {
  const headerTime = dayjs(batch.createdAt).format('YYYY-MM-DD HH:mm:ss');
  const isCreate = batch.action === 'CREATE';

  let snapshot: CreateSnapshot | null = null;
  if (isCreate) {
    const raw = batch.rows[0]?.newValue;
    if (raw) {
      try {
        snapshot = JSON.parse(raw);
      } catch {
        snapshot = null;
      }
    }
  }

  return (
    <div
      style={{
        border: '1px solid #f0f0f0',
        borderRadius: 8,
        padding: 12,
        background: '#fafafa',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
        }}
      >
        <Typography.Text strong>{actorName(batch.actor)}</Typography.Text>
        <Tag color={isCreate ? 'green' : 'blue'}>
          {isCreate ? 'Tạo bản ghi' : `Sửa ${batch.rows.length} trường`}
        </Tag>
      </div>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {headerTime}
      </Typography.Text>

      {isCreate ? (
        <div style={{ marginTop: 8 }}>
          {snapshot ? (
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              <li>Ngày: {snapshot.date}</li>
              <li>Checkin: {formatValue('checkinTime', snapshot.checkinTime)}</li>
              <li>Checkout: {formatValue('checkoutTime', snapshot.checkoutTime)}</li>
              <li>Trạng thái: {snapshot.status}</li>
              <li>Số phút làm việc: {snapshot.workingMinutes}</li>
              <li>Nguồn: {snapshot.source}</li>
              <li>Đã duyệt: {snapshot.isApproved ? 'Có' : 'Không'}</li>
            </ul>
          ) : (
            <Typography.Text type="warning">Không đọc được snapshot.</Typography.Text>
          )}
        </div>
      ) : (
        <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 18 }}>
          {batch.rows
            .filter((r) => r.field !== '__create__')
            .map((r) => (
              <li key={r.id}>
                <Typography.Text strong>{FIELD_LABELS[r.field] || r.field}:</Typography.Text>{' '}
                {formatValue(r.field, r.oldValue)} → {formatValue(r.field, r.newValue)}
              </li>
            ))}
        </ul>
      )}

      {batch.reason && (
        <div style={{ marginTop: 8 }}>
          <Typography.Text italic type="secondary">
            Lý do: {batch.reason}
          </Typography.Text>
        </div>
      )}
    </div>
  );
};

export default AttendanceHistoryDrawer;
